import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GrausHierarquiaPage = () => {
  const [graus, setGraus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    nivel: '',
    descricao: ''
  });

  useEffect(() => {
    loadGraus();
  }, []);

  const loadGraus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/graus-hierarquia`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGraus(response.data);
    } catch (error) {
      console.error('Erro ao carregar graus de hierarquia:', error);
      toast.error('Erro ao carregar graus de hierarquia');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!formData.nivel || formData.nivel < 1) {
      toast.error('Nível hierárquico é obrigatório e deve ser maior que 0');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        nivel: parseInt(formData.nivel)
      };
      
      if (editingId) {
        await axios.put(`${API}/api/graus-hierarquia/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Grau de hierarquia atualizado com sucesso!');
      } else {
        await axios.post(`${API}/api/graus-hierarquia`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Grau de hierarquia criado com sucesso!');
      }
      
      resetForm();
      loadGraus();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar grau de hierarquia');
    }
  };

  const handleEdit = (grau) => {
    setEditingId(grau.id);
    setFormData({
      nome: grau.nome,
      nivel: grau.nivel.toString(),
      descricao: grau.descricao || ''
    });
    setShowNewForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente deletar este grau de hierarquia?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/graus-hierarquia/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Grau de hierarquia deletado com sucesso!');
      loadGraus();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar grau de hierarquia');
    }
  };

  const resetForm = () => {
    setFormData({ nome: '', nivel: '', descricao: '' });
    setEditingId(null);
    setShowNewForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold">
                Graus de Hierarquia
              </CardTitle>
              <CardDescription>
                Gerencie os graus de hierarquia da organização
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowNewForm(!showNewForm)}
              className="flex items-center gap-2"
            >
              {showNewForm ? <X size={16} /> : <Plus size={16} />}
              {showNewForm ? 'Cancelar' : 'Novo Grau'}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {showNewForm && (
            <Card className="mb-6 border-2 border-blue-200">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nome">Nome *</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="Ex: Diretor, Gerente, Coordenador..."
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="nivel">Nível Hierárquico *</Label>
                      <Input
                        id="nivel"
                        type="number"
                        min="1"
                        value={formData.nivel}
                        onChange={(e) => setFormData({ ...formData, nivel: e.target.value })}
                        placeholder="Ex: 1 (mais alto), 5 (mais baixo)"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Quanto menor o número, mais alto na hierarquia
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="descricao">Descrição (opcional)</Label>
                    <Textarea
                      id="descricao"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      placeholder="Descreva as responsabilidades deste nível..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" className="flex items-center gap-2">
                      <Save size={16} />
                      {editingId ? 'Atualizar' : 'Salvar'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Graus de Hierarquia Cadastrados</CardTitle>
            </CardHeader>
            <CardContent>
              {graus.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  Nenhum grau de hierarquia cadastrado
                </div>
              ) : (
                <div className="space-y-2">
                  {graus.map((grau) => (
                    <div
                      key={grau.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                            Nível {grau.nivel}
                          </span>
                          <h3 className="font-semibold text-lg">{grau.nome}</h3>
                        </div>
                        {grau.descricao && (
                          <p className="text-sm text-gray-600 mt-2">{grau.descricao}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(grau)}
                          className="flex items-center gap-1"
                        >
                          <Edit2 size={14} />
                          Editar
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(grau.id)}
                          className="flex items-center gap-1"
                        >
                          <Trash2 size={14} />
                          Deletar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

export default GrausHierarquiaPage;
